/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, Renderer2, ViewChild } from '@angular/core';
import { Player } from 'fl-player-interface';
import { KeyNumber } from 'qp-navigation';
import { PromoGroup } from '../../shared/models/PromoGroup';
import { PlayerSeekbar } from './player-seekbar.interface';

@Component({
  selector: 'app-player-seekbar',
  templateUrl: './player-seekbar.component.html',
  styleUrls: ['./player-seekbar.component.scss'],
})
export class PlayerSeekbarComponent implements OnInit, OnDestroy {
  @ViewChild('bar')
  bar: ElementRef;

  @Output() isSeeking = new EventEmitter<PlayerSeekbar>();
  @Output() endSeek = new EventEmitter<PlayerSeekbar>();
  @Output() enter = new EventEmitter<PlayerSeekbar>();
  @Output() liveSeeking = new EventEmitter<KeyNumber>();
  @Input() max: number;
  @Input() value: number;
  @Input() step: number;
  @Input() isLive: boolean;
  @Input() cuepoints: PromoGroup[];
  @Input() isAd = false;
  @Input() playerInstance: Player;
  @Input() keyframesEnabled = false;
  @Input() showCuepoints = false;

  thumbnails: string[] = []; 
  currentTimePressed = 0;
  seeking = false;
  seekingWithMouse = false;

  private click: () => void;
  private mouseup: () => void;
  private mousemove: () => void;

  private internalStep = 10;
  private firstStep = true;
  private offsetLeft: number;
  constructor(private renderer: Renderer2, private el: ElementRef) {}

  ngOnInit(): void {
    if (this.isLive) return;
    this.handleClick();
    this.handleMouseMove();
    this.handleMouseUp();
  }

  onKeyDown(event: any): void {
    // TODO: Increase time as expected
    // Increasing time depending on key down
    this.currentTimePressed += 1;
    if (this.currentTimePressed % this.step === 0) {
      this.internalStep += this.currentTimePressed / 10;
    }

    if ([KeyNumber.LEFT, KeyNumber.RIGHT].includes(event.keyCode)) {
      const sign = KeyNumber.RIGHT === event.keyCode ? +1 : -1;
      this.seeking = true;
      const value = this.value + sign * this.internalStep;
      if (value < 0) {
        this.value = 0;
      } else if (value > this.max) {
        this.value = this.max;
      } else {
        this.value = value;
      }
      
      this.isSeeking.emit({ value: this.value, keyCode: event.keyCode });
      if (this.firstStep) {
        this.renderer.addClass(this.el.nativeElement, 'transition');
        this.internalStep = this.step;
        this.firstStep = false;
      } else {
        this.handleThumbnail();
      }
    } else if (KeyNumber.ENTER === event.keyCode) {
      this.enter.emit();
    }
  }

  onKeyUp(event: any): void {
    if (this.seeking && [KeyNumber.LEFT, KeyNumber.RIGHT].includes(event.keyCode)) {
      this.handleChange();
      // Resetting step
      this.currentTimePressed = 0;
      this.internalStep = 10;
    }
  }

  onMouseDown(): void {
    this.renderer.addClass(this.el.nativeElement, 'transition');
    this.offsetLeft = this.getOffsetLeft(this.el.nativeElement);
    this.seekingWithMouse = true;
    this.isSeeking.emit({ value: this.value });
  }

  onFocusOut() {
    if (this.seeking) {
      this.handleChange();
    }
  }

  ngOnDestroy() {
    if (this.isLive) return;
    this.click();
    this.mouseup();
    this.mousemove();
  }

  onKeyUpLive(event): void {
    if (KeyNumber.ENTER === event.keyCode) {
      this.enter.emit();
    }
  }

  onKeyDownLive(event): void {
    if (event.keyCode === KeyNumber.RIGHT || event.keyCode === KeyNumber.LEFT) {
      this.liveSeeking.emit(event.keyCode);
    }
  }

  private handleMouseUp(): void {
    this.mouseup = this.renderer.listen('document', 'mouseup', () => {
      if (this.seekingWithMouse) {
        this.seekingWithMouse = false;
        this.renderer.removeClass(this.el.nativeElement, 'transition');
        this.endSeek.emit({ value: this.value });
      }
    });
  }

  private handleMouseMove(): void {
    this.mousemove = this.renderer.listen('document', 'mousemove', (e) => {
      if (this.seekingWithMouse) {
        const x = e.clientX - this.offsetLeft;
        const width = this.bar.nativeElement.offsetWidth;
        const value = (x / width) * this.max;
        if (value < 0) {
          this.value = 0;
        } else if (value > this.max) {
          this.value = this.max;
        } else {
          this.value = value;
        }
        this.isSeeking.emit({ value: this.value });
        this.handleThumbnail();
      }
    });
  }

  private async handleThumbnail() {
    if (!this.keyframesEnabled) return;
    if (this.playerInstance) {
      const thumbnailsResponse = await Promise.all([
        this.playerInstance.getThumbnail(this.value - this.step * 2),
        this.playerInstance.getThumbnail(this.value - this.step),
        this.playerInstance.getThumbnail(this.value), // current thumbnail
        this.playerInstance.getThumbnail(this.value + this.step),
        this.playerInstance.getThumbnail(this.value + this.step * 2),
      ]);
      this.thumbnails = thumbnailsResponse.map(thumbnail => thumbnail ? thumbnail.urls[0] : '');
    }
  }

  private handleClick(): void {
    this.click = this.renderer.listen(this.el.nativeElement, 'click', (e) => {
      const className = e?.srcElement?.className || '';
      if (!className.includes('button')) {
        this.offsetLeft = this.getOffsetLeft(this.el.nativeElement);
        const x = e.clientX - this.offsetLeft;
        const width = this.bar.nativeElement.offsetWidth;
        const value = (x / width) * this.max;
        this.value = value;
        this.endSeek.emit({ value });
      }
    });
  }

  private handleChange(): void {
    this.endSeek.emit({ value: this.value });
    this.seeking = false;
    this.internalStep = 10;
    this.firstStep = true;
    this.renderer.removeClass(this.el.nativeElement, 'transition');
  }

  private getOffsetLeft(el: any): number {
    if (el?.offsetParent) {
      return el.offsetLeft + (this.getOffsetLeft(el.offsetParent) || 0);
    } else {
      return el.offsetLeft || 0;
    }
  }
}
